import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Send } from "lucide-react";
import type { GroomingRunnerEvent } from "@ttc-spectree/toolbox-contract";
import { BriefStateChip } from "@/components/grooming/brief-state-chip";
import { NextInLineCard } from "@/components/grooming/next-in-line-card";
import { QueueBadge, useEnqueueItem, type QueueStart } from "@/components/grooming/queue-status-badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { groomingKeys, useGroomingBatch, useProjectSession } from "@/hooks/queries/use-grooming";
import { useMe } from "@/hooks/queries/use-me";
import { canOn, useToolboxAccess } from "@/hooks/queries/use-toolbox-access";
import { usePostHeldBrief, useSkipHeldBrief, useUnskipBrief, useWorkItem, workItemKeys } from "@/hooks/queries/use-work-items";
import type { GroomingBatch, GroomingProjectSession } from "@/lib/api/grooming";
import type { GroomingBrief, PostHeldBriefResponse, WorkItemAddress } from "@/lib/api/work-items";
import { groomingApiErrorCode, groomingErrorMessage } from "@/lib/grooming/error-messages";
import { dateParts } from "@/lib/grooming/agenda-run";
import { showsNextInLineCard } from "@/lib/grooming/next-in-line";
import { POSTED_COMMENT_STATUSES, briefStateOf, groomCommand, type BriefState } from "@/lib/grooming/plain-words";
import { useRunnerBridge, useRunnerEvents } from "@/lib/grooming/runner-bridge";
import { cn } from "@/lib/utils";

/**
 * The brief's state, preparation line and actions, wherever a brief opens
 * (epic PI-SPEC-SW-E289, feature PI-SPEC-SW-1472, UX-PM-02; Claude Design
 * "PM visual review" frame 1b): the reading pane, the session run Workspace
 * and One item show the same chip, words and Hold back / Post to ADO.
 *
 * Who may post is unchanged (01-roles.md §9.1, 2026-10-19): `manage-agenda`
 * posts any brief; `post-single-brief` posts a single-item brief its holder
 * ran; everyone else sees the state alone. Hold back and Undo follow the same
 * rule, with or without a run (the API's assertCanReview checks all three).
 * The web cannot see whether a brief is linked to a multi-item run when no
 * `batchId` is given, so it applies the single-brief rule then and relies on
 * the API's refusal (postErrorText) for that rare case.
 *
 * Posting is one click, never retried automatically, and never touches Story
 * Points; it edits the item's one ADO comment in place.
 */

/** Held or failed briefs are the ones a person may still post. */
export function isPostable(brief: Pick<GroomingBrief, "commentStatus">): boolean {
  return brief.commentStatus === "held" || brief.commentStatus === "failed";
}

// ---------------------------------------------------------------------------
// Words
// ---------------------------------------------------------------------------

/**
 * Words for GROOMING_POST_FORBIDDEN (UX-PM-10), matching assertCanReview: a
 * single-item brief is posted by its author (with `post-single-brief`) or by
 * anyone with `manage-agenda` (Project Manager, Admin). Re-exported from
 * brief-review.tsx for the gate-copy test and Post all.
 */
export const POST_FORBIDDEN_TEXT = "Project Managers, Admins and the person who ran this brief can post it.";

/** Words for a post that SpecTree refused. */
export function postErrorText(error: unknown): string {
  switch (groomingApiErrorCode(error)) {
    case "GROOMING_BRIEF_STALE":
      return "The item changed since this brief. Re-run it, or post anyway from its review.";
    case "GROOMING_POST_FORBIDDEN":
      return POST_FORBIDDEN_TEXT;
    case "TOOLBOX_CAPABILITY_REQUIRED":
      return "Your Toolbox role on this project does not include posting this brief.";
    case "GROOMING_BRIEF_NOT_HELD":
      return "This brief was already posted, held back or replaced by a newer one.";
    case "ADO_PAT_INVALID":
    case "ADO_PAT_REQUIRED":
      return "Your ADO personal access token needs attention in Settings.";
    default:
      return "The comment was not posted. Try again in a moment.";
  }
}

/** Words for a post that reached SpecTree but not ADO. */
function postFailureText(result: PostHeldBriefResponse): string {
  const reason = result.comment.error?.message;
  return `ADO did not accept the comment${reason ? `: ${reason}` : ""}. The brief stays held.`;
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * "Oct 14" for a timestamp or a `YYYY-MM-DD` session date. A date-only value
 * is read as a local calendar day, never as UTC midnight (which shows the
 * day before west of Greenwich).
 */
export function briefDay(value: string): string {
  const date = DATE_ONLY.test(value) ? dateParts(value) : new Date(value);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function sameDay(a: string, b: string): boolean {
  return briefDay(a) === briefDay(b) && new Date(a).getFullYear() === new Date(b).getFullYear();
}

/**
 * The line beside the state chip (frame 1b): "Prepared Oct 14 for the Oct 16
 * session" for an item on the session's agenda, "Prepared Oct 14" otherwise,
 * "Posted Oct 14 by Aaron Gorsky" once posted (with ". Prepared Oct 13" when
 * that was another day), "Held back · prepared Oct 14" when held back.
 */
export function preparationLine(input: {
  brief: Pick<GroomingBrief, "commentStatus" | "createdAt" | "createdBy" | "postedAt" | "postedBy">;
  sessionDate: string | null;
  inAgenda: boolean;
}): string {
  const { brief, sessionDate, inAgenda } = input;
  const prepared = briefDay(brief.createdAt);
  if (POSTED_COMMENT_STATUSES.has(brief.commentStatus)) {
    // A brief saved by /spectree:ado-groom outside a run is posted as it is saved: no postedAt.
    const postedAt = brief.postedAt ?? brief.createdAt;
    const poster = brief.postedBy ?? (brief.postedAt ? null : (brief.createdBy ?? null));
    const posted = `Posted ${briefDay(postedAt)}${poster ? ` by ${poster.name}` : ""}`;
    return sameDay(postedAt, brief.createdAt) ? posted : `${posted}. Prepared ${prepared}`;
  }
  if (brief.commentStatus === "skipped") return `Held back · prepared ${prepared}`;
  return `Prepared ${prepared}${inAgenda && sessionDate ? ` for the ${briefDay(sessionDate)} session` : ""}`;
}

/**
 * The session an item's brief is for: inside a run, that run (an agenda run's
 * own date, else the next meeting date); elsewhere the shared agenda, when
 * the item is on it.
 */
export function sessionContext(
  itemId: string,
  session: GroomingProjectSession | undefined,
  batch: GroomingBatch | undefined
): { sessionDate: string | null; inAgenda: boolean } {
  if (batch) {
    const inAgenda = batch.mode === "agenda";
    return { inAgenda, sessionDate: inAgenda ? (batch.sessionDate ?? session?.nextMeetingDate ?? null) : null };
  }
  const agenda = session?.batch ?? null;
  const inAgenda = Boolean(agenda?.items.some((entry) => entry.itemId === itemId));
  return { inAgenda, sessionDate: inAgenda ? (session?.sessionDate ?? session?.nextMeetingDate ?? null) : null };
}

// ---------------------------------------------------------------------------
// The stale confirm and re-running one item
// ---------------------------------------------------------------------------

/** "The item changed since this brief. Re-run it, or post anyway?" */
export function StaleConfirmDialog({
  open,
  onOpenChange,
  onPostAnyway,
  onRerun,
  rerunCommand,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPostAnyway: () => void;
  /** Re-run in SpecTree Toolbox; null in a browser, which shows the slash command instead. */
  onRerun: (() => void) | null;
  rerunCommand: string;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="stale-confirm">
        <DialogHeader>
          <DialogTitle>The item changed since this brief</DialogTitle>
          <DialogDescription>The item changed since this brief. Re-run it, or post anyway?</DialogDescription>
        </DialogHeader>
        {onRerun === null && (
          <p className="text-sm text-muted-foreground">
            To re-run it, open SpecTree Toolbox or run <code className="font-mono text-[13px]">{rerunCommand}</code> in Claude
            Code.
          </p>
        )}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            Cancel
          </Button>
          {onRerun && (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onOpenChange(false);
                onRerun();
              }}
            >
              Re-run it
            </Button>
          )}
          <Button
            type="button"
            className="border-primary bg-primary text-primary-foreground hover:bg-primary/90"
            onClick={() => {
              onOpenChange(false);
              onPostAnyway();
            }}
          >
            Post anyway
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

type GroomOneState = { status: "idle" | "running" | "done" } | { status: "failed"; message: string };

/**
 * Groom one item in SpecTree Toolbox (Retry this item, Re-run it, One item)
 * and refresh the reads when Claude finishes it. In a contract-3 app (epic
 * PI-SPEC-SW-E284) `start` adds the item to the run queue instead, `queue`
 * says whether it is already queued or running, and `state` reads
 * `running` while it is.
 */
export function useGroomOneItem(project: string, itemId: string, batchId?: string) {
  const { runner } = useRunnerBridge();
  const queryClient = useQueryClient();
  const [state, setState] = useState<GroomOneState>({ status: "idle" });

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: workItemKeys.detail("ado", project, itemId) });
    if (batchId) void queryClient.invalidateQueries({ queryKey: groomingKeys.batch(batchId) });
  }, [project, itemId, batchId, queryClient]);

  const onEvent = useCallback(
    (event: GroomingRunnerEvent) => {
      if (event.type !== "item-finished" || event.itemId !== itemId) return;
      setState(
        event.outcome === "failed"
          ? { status: "failed", message: groomingErrorMessage(event.errorCode).cause }
          : { status: "done" }
      );
      refresh();
    },
    [itemId, refresh]
  );
  useRunnerEvents(runner, onEvent);
  const queue: QueueStart = useEnqueueItem(project, itemId, refresh);

  if (queue.available) {
    const start = () => {
      setState({ status: "idle" });
      void queue.enqueue().then((result) => {
        if (result && !result.ok) setState({ status: "failed", message: result.message });
      });
    };
    const queued: GroomOneState = queue.active || queue.pending ? { status: "running" } : state;
    return { state: queued, start, queue };
  }

  const start = runner
    ? () => {
        setState({ status: "running" });
        void runner.groomOne(project, itemId).then(
          (result) => {
            if (!result.ok) setState({ status: "failed", message: groomingErrorMessage(result.errorCode).cause });
          },
          () => {
            setState({ status: "failed", message: "SpecTree Toolbox could not start the run." });
          }
        );
      }
    : null;

  return { state, start, queue };
}

// ---------------------------------------------------------------------------
// Enter to post (the Workspace only)
// ---------------------------------------------------------------------------

/** True when a key press was meant for a field, button, link or dialog rather than the page. */
function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  if (target.closest("input, textarea, select, button, a, [role='dialog'], [role='menu'], [role='listbox']")) return true;
  return false;
}

/** Call `onEnter` when Enter is pressed on the page itself, while `enabled`. */
function useEnterShortcut(enabled: boolean, onEnter: () => void): void {
  const handler = useRef(onEnter);
  useEffect(() => {
    handler.current = onEnter;
  }, [onEnter]);
  useEffect(() => {
    if (!enabled) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Enter" || event.repeat || event.defaultPrevented) return;
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      if (isTypingTarget(event.target)) return;
      event.preventDefault();
      handler.current();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [enabled]);
}

// ---------------------------------------------------------------------------
// The actions hook
// ---------------------------------------------------------------------------

export interface BriefReviewOutcome {
  tone: "ok" | "error";
  text: string;
}

/**
 * Who may post `brief`, by the rule the API's assertCanReview applies (E283):
 * `manage-agenda` posts any brief; with no run around it, `post-single-brief`
 * posts the caller's own brief.
 */
export function mayPostBrief(input: {
  brief: Pick<GroomingBrief, "createdBy"> | null;
  manageAgenda: boolean;
  postSingleBrief: boolean;
  meId: string | undefined;
  batchId: string | undefined;
}): boolean {
  const { brief, manageAgenda, postSingleBrief, meId, batchId } = input;
  if (brief === null) return false;
  if (manageAgenda) return true;
  return batchId === undefined && postSingleBrief && meId !== undefined && brief.createdBy?.id === meId;
}

/**
 * Post, Hold back and Undo for the brief on one item, with the stale confirm
 * (`dialog`, render it once). Reads the item with useWorkItem, so it shares
 * the one live ADO read of whatever shows the brief.
 */
export function useBriefReviewActions({ project, itemId, batchId }: { project: string; itemId: string; batchId?: string | undefined }) {
  const { data: me } = useMe();
  const { data: access } = useToolboxAccess();
  const address: WorkItemAddress = { tracker: "ado", container: project, id: itemId };
  // The pane and the brief review already read the item: ride along on that read (one live ADO read).
  const itemQuery = useWorkItem("ado", project, itemId, { rideAlong: true });
  const post = usePostHeldBrief(address);
  const skip = useSkipHeldBrief(address);
  const unskip = useUnskipBrief(address);
  const rerun = useGroomOneItem(project, itemId, batchId);
  const [confirmStale, setConfirmStale] = useState(false);
  const [outcome, setOutcome] = useState<BriefReviewOutcome | null>(null);

  const item = itemQuery.data;
  const block = item?.groomingBrief ?? null;
  const brief = block?.brief ?? null;
  const stale = block?.stale ?? false;

  const state: BriefState | null = brief ? briefStateOf({ commentStatus: brief.commentStatus, stale }) : null;
  // Posted and held back from the comment status alone: out of date wins in `state`, not here.
  const ownState: BriefState | null = brief ? briefStateOf({ commentStatus: brief.commentStatus }) : null;
  const posted = ownState === "posted";
  const heldBack = ownState === "held-back";
  const mayPost = mayPostBrief({
    brief,
    manageAgenda: canOn(access, project, "manage-agenda"),
    postSingleBrief: canOn(access, project, "post-single-brief"),
    meId: me?.id,
    batchId,
  });
  const mayRerun = canOn(access, project, "groom-one");
  const canPostNow = brief !== null && !posted && !heldBack && isPostable(brief) && mayPost;
  const busy = post.isPending || skip.isPending || unskip.isPending;

  const doPost = (force: boolean) => {
    if (!brief) return;
    setOutcome(null);
    post.mutate(
      { briefId: brief.id, force },
      {
        onSuccess: (result) => {
          setOutcome(
            result.comment.status === "failed"
              ? { tone: "error", text: postFailureText(result) }
              : { tone: "ok", text: "Posted to ADO." }
          );
        },
        onError: (error) => {
          if (groomingApiErrorCode(error) === "GROOMING_BRIEF_STALE") {
            setConfirmStale(true);
            return;
          }
          setOutcome({ tone: "error", text: postErrorText(error) });
        },
      }
    );
  };

  const requestPost = () => {
    if (!canPostNow || busy) return;
    if (stale) setConfirmStale(true);
    else doPost(false);
  };

  const holdBack = () => {
    if (!brief || !canPostNow || busy) return;
    setOutcome(null);
    skip.mutate(brief.id, {
      onError: () => {
        setOutcome({ tone: "error", text: "The brief could not be held back. Try again in a moment." });
      },
    });
  };

  const undo = () => {
    if (!brief || !heldBack || !mayPost || busy) return;
    setOutcome(null);
    unskip.mutate(brief.id, {
      onError: () => {
        setOutcome({ tone: "error", text: "The brief could not be brought back. Try again in a moment." });
      },
    });
  };

  const dialog = (
    <StaleConfirmDialog
      open={confirmStale}
      onOpenChange={setConfirmStale}
      onPostAnyway={() => {
        doPost(true);
      }}
      onRerun={mayRerun ? rerun.start : null}
      rerunCommand={groomCommand(itemId)}
    />
  );

  return {
    itemQuery,
    item,
    block,
    brief,
    state,
    mayPost,
    canPostNow,
    heldBack,
    posted,
    busy,
    posting: post.isPending,
    confirmStale,
    outcome,
    requestPost,
    holdBack,
    undo,
    rerun,
    dialog,
  };
}

export type BriefReviewActions = ReturnType<typeof useBriefReviewActions>;

// ---------------------------------------------------------------------------
// The bar
// ---------------------------------------------------------------------------

export type BriefStateBarVariant = "pane" | "review";

export interface BriefStateBarProps {
  project: string;
  itemId: string;
  /** The run the brief belongs to (Workspace); none in the pane and One item. */
  batchId?: string | undefined;
  /** `pane`: frame 1b's bar under the reading pane's header. `review`: the brief review's footer row. */
  variant: BriefStateBarVariant;
  /** Enter posts when nothing else has focus (the Workspace, with `manage-agenda`). Never in the pane. */
  enterToPost?: boolean;
  /** Shown before the buttons in the review footer (One item's Copy brief). */
  extra?: ReactNode;
}

/** Hold back: frame 1b's secondary button (30 px, #1A1A1E, #27272A border). */
function SecondaryButton({ children, onClick, disabled }: { children: ReactNode; onClick: () => void; disabled: boolean }) {
  return (
    <button
      type="button"
      className="inline-flex h-[30px] items-center gap-1.5 whitespace-nowrap rounded-lg border border-[#27272A] bg-[#1A1A1E] px-3 text-[13px] font-medium text-foreground transition-colors hover:bg-[#222227] disabled:opacity-50"
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

/**
 * Where a re-run of the item stands ("Re-run it", Retry): its queue place in
 * a contract-3 app, Claude still running it, or why it failed. Both variants
 * show it, so a re-run started from the pane's stale dialog is visible there.
 */
function RerunStatus({
  rerun,
  project,
  className,
}: {
  rerun: BriefReviewActions["rerun"];
  project: string;
  className?: string;
}) {
  const active = rerun.queue.active;
  // E290 (UX-PM-04, frame B4): a single item waiting in Up next says where it is, in the compact card.
  if (active?.kind === "queued" && showsNextInLineCard(active.entry)) {
    return (
      <div className={className}>
        <NextInLineCard entryId={active.entry.id} project={project} size="compact" />
      </div>
    );
  }
  if (rerun.queue.active) {
    return (
      <p className={cn("flex items-center gap-2 text-sm text-muted-foreground", className)} role="status">
        <QueueBadge status={rerun.queue.active} />
        The new brief appears here when it finishes.
      </p>
    );
  }
  if (rerun.state.status === "running") {
    return (
      <p className={cn("text-sm text-muted-foreground", className)} role="status">
        Claude is re-running this brief. It appears here when it finishes.
      </p>
    );
  }
  if (rerun.state.status === "failed") {
    return (
      <p className={cn("text-sm text-destructive", className)} role="alert">
        {rerun.state.message}
      </p>
    );
  }
  return null;
}

/**
 * The state chip, the preparation line and, for people who may post, Hold
 * back (or Undo) and Post to ADO, with the outcome of the last action and
 * any re-run under it. Nothing when the item has no brief.
 */
export function BriefStateBar({ project, itemId, batchId, variant, enterToPost = false, extra }: BriefStateBarProps) {
  const actions = useBriefReviewActions({ project, itemId, batchId });
  const session = useProjectSession(project);
  const batch = useGroomingBatch(batchId);
  useEnterShortcut(enterToPost && variant === "review" && actions.canPostNow && !actions.confirmStale, actions.requestPost);

  const { brief, state } = actions;
  if (!brief || !state) return null;

  const context = sessionContext(itemId, session.data, batchId ? batch.data : undefined);
  const line = preparationLine({ brief, ...context });
  const pane = variant === "pane";
  // Once the refreshed brief reads as posted, the chip says so; don't repeat it.
  const outcome = actions.outcome && !(actions.outcome.tone === "ok" && actions.posted) ? actions.outcome : null;

  const buttons = actions.mayPost ? (
    actions.heldBack ? (
      <SecondaryButton disabled={actions.busy} onClick={actions.undo}>
        Undo
      </SecondaryButton>
    ) : actions.canPostNow ? (
      <>
        <SecondaryButton disabled={actions.busy} onClick={actions.holdBack}>
          Hold back
        </SecondaryButton>
        <Button
          type="button"
          className="h-[30px] gap-1.5 rounded-lg border-primary bg-primary px-3 text-[13px] font-semibold text-primary-foreground hover:bg-primary/90"
          disabled={actions.busy}
          onClick={actions.requestPost}
        >
          <Send className="h-3.5 w-3.5" aria-hidden="true" />
          {actions.posting ? "Posting…" : "Post to ADO"}
          {enterToPost && !pane && (
            <kbd className="ml-1 rounded border border-white/30 px-1 font-sans text-[11px] font-medium" aria-hidden="true">
              ↵
            </kbd>
          )}
        </Button>
      </>
    ) : null
  ) : null;

  const outcomeLine = outcome && (
    <p
      className={cn("text-[13px]", outcome.tone === "ok" ? "text-green-400" : "text-destructive", pane && "px-5 pb-2.5")}
      role={outcome.tone === "ok" ? "status" : "alert"}
      data-testid="review-outcome"
    >
      {outcome.text}
    </p>
  );

  if (pane) {
    return (
      <div className="flex-none border-b border-[#1F1F23] bg-[#0E0E10]" data-testid="brief-state-bar" data-variant="pane" data-state={state}>
        <div className="flex flex-wrap items-center gap-2.5 px-5 py-3">
          <BriefStateChip state={state} />
          <span className="min-w-0 flex-1 text-xs text-muted-foreground" data-testid="brief-preparation-line">
            {line}
          </span>
          {buttons}
        </div>
        {outcomeLine}
        <RerunStatus rerun={actions.rerun} project={project} className="px-5 pb-2.5 text-[13px]" />
        {actions.dialog}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2" data-testid="brief-state-bar" data-variant="review" data-state={state}>
      <div className="flex flex-wrap items-center gap-2.5 border-t border-border pt-3.5" data-testid="review-footer">
        <BriefStateChip state={state} />
        <span className="min-w-0 flex-1 text-xs text-muted-foreground" data-testid="brief-preparation-line">
          {line}
        </span>
        {extra}
        {buttons}
      </div>
      {actions.mayPost && !actions.posted && (
        <p className="text-[13px] text-muted-foreground" data-testid="post-note">
          Posts one comment to ADO as you. Story Points are never changed.
        </p>
      )}
      {outcomeLine}
      <RerunStatus rerun={actions.rerun} project={project} />
      {actions.dialog}
    </div>
  );
}
